import React, { useState, useEffect, useRef } from 'react';
import { collection, onSnapshot, addDoc, query, doc, updateDoc, serverTimestamp, orderBy, deleteDoc } from 'firebase/firestore';
import { ref, uploadBytesResumable, getDownloadURL, deleteObject } from "firebase/storage";

// --- Iconos ---
const IconPencil = () => <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.5L13.196 5.196z"></path></svg>;
const IconTrash = () => <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path></svg>;
const IconX = () => <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd"></path></svg>;
const IconCamera = () => <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z"></path><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 13a3 3 0 11-6 0 3 3 0 016 0z"></path></svg>;
const IconMic = () => <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z"></path></svg>;
const IconTask = () => <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4"></path></svg>;
const IconMove = () => <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" /></svg>;
const IconSend = () => <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20"><path d="M10.894 2.553a1 1 0 00-1.788 0l-7 14a1 1 0 001.169 1.409l5-1.429A1 1 0 009 15.571V11a1 1 0 112 0v4.571a1 1 0 00.725.962l5 1.428a1 1 0 001.17-1.408l-7-14z"></path></svg>;
const IconMore = () => <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20"><path d="M6 10a2 2 0 11-4 0 2 2 0 014 0zM12 10a2 2 0 11-4 0 2 2 0 014 0zM16 12a2 2 0 100-4 2 2 0 000 4z"></path></svg>;
const IconNote = () => <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20"><path d="M17.414 2.586a2 2 0 00-2.828 0L7 10.172V13h2.828l7.586-7.586a2 2 0 000-2.828z"></path><path fillRule="evenodd" d="M2 6a2 2 0 012-2h4a1 1 0 010 2H4v10h10v-4a1 1 0 112 0v4a2 2 0 01-2 2H4a2 2 0 01-2-2V6z" clipRule="evenodd"></path></svg>;

const TimelineItem = ({ item, user, isAdmin, onUpdate, onDelete, onToggleStatus }) => {
    const [isEditing, setIsEditing] = useState(false);
    const [editText, setEditText] = useState(item.text);
    const [showMenu, setShowMenu] = useState(false);

    const canEdit = user.uid === item.authorId || isAdmin;

    const handleUpdate = () => {
        onUpdate(item.id, editText);
        setIsEditing(false);
    };

    const renderContent = () => {
        if (isEditing) {
            return (
                <div className="flex gap-2 w-full">
                    <textarea value={editText} onChange={(e) => setEditText(e.target.value)} className="flex-grow p-1 border rounded text-sm w-full"/>
                    <div className="flex flex-col gap-1">
                        <button onClick={handleUpdate} className="px-2 text-xs bg-green-500 text-white rounded">OK</button>
                        <button onClick={() => setIsEditing(false)} className="px-2 text-xs bg-gray-400 text-white rounded">X</button>
                    </div>
                </div>
            )
        }
        switch(item.type) {
            case 'note': return <p className="flex items-start gap-2"><IconNote className="text-gray-400 mt-1 flex-shrink-0"/><span>{item.text}</span></p>;
            case 'image': return <a href={item.imageUrl} target="_blank" rel="noreferrer"><img src={item.imageUrl} alt={item.text} className="rounded-lg max-h-48"/></a>;
            case 'audio': return <audio controls src={item.audioUrl} className="w-full h-10"></audio>;
            case 'task': return <div className="flex items-center gap-2"><input type="checkbox" checked={item.status==='completado'} onChange={() => onToggleStatus(item)} className="h-4 w-4"/> <span className={`${item.status==='completado' && 'line-through'}`}>{item.text}</span></div>;
            case 'log': return <p className="italic text-gray-500">{item.text}</p>;
            default: return null;
        }
    };
    
    return (
        <div className="bg-gray-50 p-3 rounded-lg text-sm group relative">
            {renderContent()}
            <p className="text-xs text-gray-500 text-right mt-1">- {item.authorName} ({item.timestamp?.toDate().toLocaleString() || '...'})</p>
            {canEdit && !isEditing && item.type !== 'log' && (
                <div className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button onClick={() => setShowMenu(!showMenu)} onBlur={() => setTimeout(() => setShowMenu(false), 200)} className="p-1 hover:bg-gray-200 rounded-full"><IconMore /></button>
                    {showMenu && (
                         <div className="absolute right-0 mt-1 w-28 bg-white rounded-md shadow-lg z-10">
                            {(item.type === 'note' || item.type === 'task') && <button onClick={() => {setIsEditing(true); setShowMenu(false);}} className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">Editar</button>}
                            <button onClick={() => {onDelete(item); setShowMenu(false);}} className="block w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-gray-100">Eliminar</button>
                         </div>
                    )}
                </div>
            )}
        </div>
    )
};


const LeadDetailModal = ({ lead, columns, db, storage, user, onClose, onEdit, onDelete }) => {
    const [timeline, setTimeline] = useState([]);
    const [newNote, setNewNote] = useState('');
    const [isRecording, setIsRecording] = useState(false);
    const mediaRecorderRef = useRef(null);
    const fileInputRef = useRef(null);
    const [showMoveMenu, setShowMoveMenu] = useState(false);
    const isAdmin = user.permissions?.canManageRoles;

    useEffect(() => {
        const q = query(collection(db, `tradeshow_leads/${lead.id}/timeline`), orderBy('timestamp', 'desc'));
        const unsubscribe = onSnapshot(q, (snapshot) => setTimeline(snapshot.docs.map(doc => ({id: doc.id, ...doc.data()}))));
        return () => unsubscribe();
    }, [db, lead.id]);

    const addTimelineEntry = async (data) => {
        await addDoc(collection(db, `tradeshow_leads/${lead.id}/timeline`), { ...data, authorName: user.name || user.email, authorId: user.uid, timestamp: serverTimestamp() });
    };

    const handleAddNote = () => { if (newNote.trim()) { addTimelineEntry({ type: 'note', text: newNote }); setNewNote(''); } };
    const handleCreateTask = () => { const taskText = prompt("Describe la tarea o solicitud:"); if (taskText) addTimelineEntry({ type: 'task', text: taskText, status: 'pendiente' }); };
    
    const handleFileUpload = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        const storageRef = ref(storage, `leads/${lead.id}/${Date.now()}_${file.name}`);
        const uploadTask = uploadBytesResumable(storageRef, file);
        uploadTask.on('state_changed', null, 
            (error) => console.error("Upload error:", error),
            () => { getDownloadURL(uploadTask.snapshot.ref).then((downloadURL) => addTimelineEntry({ type: 'image', imageUrl: downloadURL, text: file.name }));}
        );
    };

    const toggleRecording = () => {
        if (isRecording) {
            mediaRecorderRef.current.stop();
            setIsRecording(false);
        } else {
            navigator.mediaDevices.getUserMedia({ audio: true }).then(stream => {
                mediaRecorderRef.current = new MediaRecorder(stream);
                mediaRecorderRef.current.start();
                const audioChunks = [];
                mediaRecorderRef.current.addEventListener("dataavailable", event => audioChunks.push(event.data));
                mediaRecorderRef.current.addEventListener("stop", () => {
                    const audioBlob = new Blob(audioChunks);
                    const storageRef = ref(storage, `leads/${lead.id}/${Date.now()}_audio.webm`);
                    const uploadTask = uploadBytesResumable(storageRef, audioBlob);
                    uploadTask.on('state_changed', null, 
                        (error) => console.error("Audio upload error:", error),
                        () => { getDownloadURL(uploadTask.snapshot.ref).then((downloadURL) => addTimelineEntry({ type: 'audio', audioUrl: downloadURL, text: 'Nota de voz' }));}
                    );
                });
                setIsRecording(true);
            }).catch(err => console.error("Error accessing microphone:", err));
        }
    };
    
    const handleMoveLead = async (newStatus) => {
        if (lead.status === newStatus) return;
        try {
            const leadRef = doc(db, "tradeshow_leads", lead.id);
            await updateDoc(leadRef, { status: newStatus });
            const endColumnTitle = columns[newStatus]?.title || newStatus;
            const timelineRef = collection(db, `tradeshow_leads/${lead.id}/timeline`);
            await addDoc(timelineRef, { type: 'log', text: `Estado cambiado manualmente a: ${endColumnTitle}.`, authorName: user.name || "Sistema", authorId: user.uid, timestamp: serverTimestamp() });
            setShowMoveMenu(false);
            onClose();
        } catch (error) {
            console.error("Error moving lead:", error);
        }
    };
    
    const handleUpdateItem = async (itemId, newText) => {
        const itemRef = doc(db, `tradeshow_leads/${lead.id}/timeline`, itemId);
        await updateDoc(itemRef, { text: newText });
    };

    const handleDeleteItem = async (item) => {
        if (!window.confirm("¿Estás seguro de que quieres eliminar este elemento?")) return;
        await deleteDoc(doc(db, `tradeshow_leads/${lead.id}/timeline`, item.id));

        if ((item.type === 'image' || item.type === 'audio') && (item.audioUrl || item.imageUrl)) {
            try {
                const url = item.audioUrl || item.imageUrl;
                const fileRef = ref(storage, url);
                await deleteObject(fileRef);
            } catch (error) {
                if (error.code !== 'storage/object-not-found') {
                    console.error("Error deleting file from Storage:", error);
                }
            }
        }
    };
    
    const toggleTaskStatus = async (item) => {
        const newStatus = item.status === 'pendiente' ? 'completado' : 'pendiente';
        await updateDoc(doc(db, `tradeshow_leads/${lead.id}/timeline`, item.id), { status: newStatus });
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-40 p-4">
            <div className="bg-white p-6 rounded-lg shadow-xl w-full max-w-2xl flex flex-col h-[90vh]">
                 <div className="flex justify-between items-center border-b pb-3 mb-4">
                     <div><h2 className="text-2xl font-bold">{lead.contactName}</h2><p className="text-gray-600">{lead.company}</p></div>
                     <div className="flex items-center gap-2 relative">
                         <div className="relative">
                            <button onClick={() => setShowMoveMenu(!showMoveMenu)} className="p-2 rounded-full hover:bg-gray-200 text-gray-600 hover:text-green-600" title="Mover Lead"><IconMove/></button>
                            {showMoveMenu && (
                                <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg z-20">
                                    {Object.values(columns).map(col => (
                                        <button key={col.id} onClick={() => handleMoveLead(col.id)} className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">Mover a "{col.title}"</button>
                                    ))}
                                </div>
                            )}
                         </div>
                         <button onClick={onEdit} className="p-2 rounded-full hover:bg-gray-200 text-gray-600 hover:text-blue-600" title="Editar"><IconPencil/></button>
                         <button onClick={onDelete} className="p-2 rounded-full hover:bg-gray-200 text-gray-600 hover:text-red-600" title="Eliminar"><IconTrash/></button>
                         <button onClick={onClose} className="p-2 rounded-full hover:bg-gray-200"><IconX/></button>
                     </div>
                 </div>
                 <div className="flex-grow overflow-y-auto pr-2">
                    <div className="mb-4 text-sm"><p><strong>Email:</strong> {lead.email || 'N/A'}</p><p><strong>Teléfono:</strong> {lead.phone}</p><p><strong>Interés:</strong> <span className="capitalize">{lead.interest || 'N/A'}</span></p><p className="mt-2"><strong>Notas Iniciales:</strong> {lead.notes || 'N/A'}</p></div>
                     <h3 className="font-bold mt-6 border-t pt-4">Timeline de Actividad</h3>
                     {/* --- LÓGICA DE RENDERIZADO RESTAURADA --- */}
                     <div className="space-y-3 mt-2">
                         {timeline.map(item => (
                             <TimelineItem 
                                key={item.id} 
                                item={item} 
                                user={user} 
                                isAdmin={isAdmin}
                                onUpdate={handleUpdateItem}
                                onDelete={handleDeleteItem}
                                onToggleStatus={toggleTaskStatus}
                             />
                         ))}
                     </div>
                 </div>
                 <div className="mt-4 border-t pt-4 flex gap-2 items-center">
                    <input value={newNote} onChange={(e) => setNewNote(e.target.value)} onKeyPress={(e) => e.key === 'Enter' && handleAddNote()} placeholder="Añadir una nota..." className="flex-grow p-2 border rounded-lg" />
                    <button onClick={handleAddNote} className="bg-blue-600 text-white p-2 rounded-lg hover:bg-blue-700"><IconSend/></button>
                 </div>
                 <div className="mt-2 pt-2 border-t flex justify-around items-center text-gray-600">
                    <input type="file" accept="image/*" capture="environment" ref={fileInputRef} onChange={handleFileUpload} className="hidden" />
                    <button onClick={() => fileInputRef.current.click()} className="p-2 hover:bg-gray-100 rounded-full" title="Tomar Foto"><IconCamera/></button>
                    <button onClick={toggleRecording} className={`p-2 hover:bg-gray-100 rounded-full ${isRecording ? 'text-red-500 animate-pulse' : ''}`} title="Grabar Nota de Voz"><IconMic/></button>
                    <button onClick={handleCreateTask} className="p-2 hover:bg-gray-100 rounded-full" title="Crear Tarea"><IconTask/></button>
                 </div>
            </div>
        </div>
    );
};

export default LeadDetailModal;

